import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ConfirmModal } from '../../components/ConfirmModal/ConfirmModal';
import { useOnboardingStore } from '../../stores/onboardingStore';
import { RelanceOnboardingStatus } from '../../types/onboarding';
import { useSyncStore } from '../../stores/syncStore';
import { useTasksStore } from '../../stores/tasksStore';
import { useAcombaBridgeStore } from '../../stores/acombaBridgeStore';
import { useIsAcomba } from '../../hooks/useIsAcomba';
import { useSync } from '../../hooks/useSync';
import { SyncType } from '../../types/sync/enums';
import { onboardingService } from '../../services/onboardingService';
import { TranslationKey } from '../../i18n/translationKeys';
import { OnboardingFooter } from './OnboardingFooter';
import { ScreenWelcome } from './ScreenWelcome';
import { ScreenValueProps } from './ScreenValueProps';
import { ScreenConsent } from './ScreenConsent';
import { ScreenRefused } from './ScreenRefused';
import { AssistantUnavailable } from '../AssistantUnavailable/AssistantUnavailable';
import { lastOnboardingStep, type OnboardingStep } from './types';
import { featureFlags } from '../../utils/featureFlags';

/**
 * The relance onboarding walkthrough (see RootLayout/useOnboardingGate). Two things happen here, and
 * they have different scopes.
 *
 * Screen 1 welcomes the person: per user, once, whatever the company has already decided — a newcomer
 * to a company that accepted long ago still gets it. The screens after it ask the company's question,
 * so only an administrator of a company that has not yet decided sees them. Anyone else is told an
 * administrator must approve, and their progress dots say one screen rather than promising screens
 * they will never reach.
 */
export const Onboarding: React.FC = () => {
    const { t } = useTranslation();
    const [step, setStep] = useState<OnboardingStep>(1);
    const [consentChecked, setConsentChecked] = useState(false);
    const [confirmingRefusal, setConfirmingRefusal] = useState(false);
    const [refused, setRefused] = useState(false);
    const [completing, setCompleting] = useState(false);
    const isAdministrator = useOnboardingStore(s => s.isAdministrator);
    const hasSeenWelcome = useOnboardingStore(s => s.hasSeenWelcome);
    const status = useOnboardingStore(s => s.status);
    const setOnboardingRequired = useOnboardingStore(s => s.setOnboardingRequired);
    const setStatus = useOnboardingStore(s => s.setStatus);
    const setInitialSyncRequired = useSyncStore(s => s.setInitialSyncRequired);
    const { trigger } = useSync();
    const isAcomba = useIsAcomba();
    const checkBridgeHealth = useAcombaBridgeStore(s => s.checkBridgeHealth);
    const fetchDailyTasks = useTasksStore(s => s.fetchDailyTasks);

    const decisionPending = status === RelanceOnboardingStatus.NotStarted;
    const showsDecisionScreens = decisionPending && isAdministrator;
    const awaitsApproval = decisionPending && !isAdministrator;
    const lastStep = lastOnboardingStep(showsDecisionScreens);
    const [pastWelcome, setPastWelcome] = useState(hasSeenWelcome);

    const goBack = () => setStep((s) => (s > 1 ? ((s - 1) as OnboardingStep) : s));

    // Mirrors InitialSync's own bridge/Connect gate — the walkthrough is relance-only, so a denied
    // user has nothing to sync; skipping avoids a generic "sync failed, retry" for what is really
    // "no access".
    const isRelanceDenied = async (): Promise<boolean> => {
        if (isAcomba) {
            await checkBridgeHealth();
            const access = useAcombaBridgeStore.getState().functionalityAccess;
            return access !== null && !access.relance;
        }
        await fetchDailyTasks();
        return useTasksStore.getState().dailyTasks?.relanceAccessible === false;
    };

    // Records consent first, then closes the overlay — the backend gate (SyncService/
    // BridgeSyncService) checks for exactly this Completed decision, so ordering these two calls the
    // other way around would have the trigger rejected. The sync itself is fired in the background and
    // never awaited by the transition: Home already knows how to show a sync's progress and outcome
    // (including a persistent failure banner), so the overlay does not need its own copy of that UI.
    const finishConsent = async () => {
        let recorded = false;
        try {
            await onboardingService.complete();
            recorded = true;
        } catch (e) {
            // Best-effort: if this failed to persist, the trigger below is rejected server-side
            // (onboardingNotCompleted) and surfaces through Home's own sync-failure banner.
            console.error('Failed to record onboarding consent:', e);
        }
        const denied = await isRelanceDenied();
        handleFinish();
        if (recorded) setStatus(RelanceOnboardingStatus.Completed);
        if (!denied) void trigger(SyncType.Full);
    };

    // Best-effort: being welcomed twice beats a failed write blocking the person.
    const recordWelcomeSeen = () => {
        void onboardingService.markWelcomeSeen().catch((e) => {
            console.error('Failed to record that the welcome screen was seen:', e);
        });
    };

    const handleNext = () => {
        // Recorded on leaving screen 1 rather than on finishing, so an administrator who goes on to the
        // decision screens is not welcomed again next time.
        if (step === 1) recordWelcomeSeen();

        if (step !== lastStep) {
            setStep((s) => (s < lastStep ? ((s + 1) as OnboardingStep) : s));
            return;
        }

        if (showsDecisionScreens) {
            setCompleting(true);
            void finishConsent().finally(() => setCompleting(false));
            return;
        }

        if (awaitsApproval) setPastWelcome(true);
        else dismissWelcome();
    };

    const handleRefuseConfirm = async () => {
        setConfirmingRefusal(false);
        try {
            await onboardingService.refuse();
        } catch (e) {
            // Best-effort: the Settings reactivation panel only renders once the backend actually
            // recorded Refused, so a failed write here does NOT surface there — the recovery path is
            // the next session's useOnboardingGate fetch, which still reads NotStarted and re-shows
            // the full walkthrough rather than silently granting access.
            console.error('Failed to record onboarding refusal:', e);
        }
        setRefused(true);
    };

    // Leaves initialSyncRequired alone, unlike handleFinish: no sync was fired here.
    const dismissWelcome = () => setOnboardingRequired(false);

    const handleFinish = () => {
        setOnboardingRequired(false);
        // The walkthrough already ran (or deliberately skipped) the initial sync — RootLayout must not
        // fall through to the separate InitialSync overlay right after.
        setInitialSyncRequired(false);
    };

    if (refused) return <ScreenRefused onContinue={handleFinish} />;
    // Derived, not latched: a desktop administrator can click past the welcome before the bridge has
    // reported isSupervisor, and must not stay stranded here once it does.
    if (pastWelcome && awaitsApproval) return <AssistantUnavailable />;

    return (
        <div data-testid="onboarding-overlay" className="flex flex-col min-h-screen justify-between px-l py-3xl">
            <div className="flex-1 flex flex-col items-center pt-3xl">
                {step === 1 && <ScreenWelcome />}
                {step === 2 && showsDecisionScreens && <ScreenValueProps />}
                {step === 3 && showsDecisionScreens && (
                    <ScreenConsent
                        checked={consentChecked}
                        onCheckedChange={setConsentChecked}
                        onRefuseClick={() => setConfirmingRefusal(true)}
                    />
                )}
            </div>

            <div className="w-full max-w-md mx-auto">
                <OnboardingFooter
                    step={step}
                    total={lastStep}
                    onBack={goBack}
                    onNext={handleNext}
                    nextDisabled={featureFlags.onboardingConsentScreen && showsDecisionScreens
                        && step === lastStep && !consentChecked}
                    nextLoading={showsDecisionScreens && step === lastStep && completing}
                    nextLabel={showsDecisionScreens && step === lastStep
                        ? t(TranslationKey.Onboarding.Nav.Continue)
                        : t(TranslationKey.Onboarding.Nav.Next)}
                />
            </div>

            {confirmingRefusal && (
                <ConfirmModal
                    testId="onboarding-refuse-confirm-modal"
                    heading={t(TranslationKey.Onboarding.RefuseConfirm.Title)}
                    body={t(TranslationKey.Onboarding.RefuseConfirm.Body)}
                    confirmLabel={t(TranslationKey.Onboarding.RefuseConfirm.Confirm)}
                    cancelLabel={t(TranslationKey.Onboarding.RefuseConfirm.Cancel)}
                    confirmBtnTestId="onboarding-refuse-confirm-btn"
                    cancelBtnTestId="onboarding-refuse-cancel-btn"
                    onConfirm={() => void handleRefuseConfirm()}
                    onCancel={() => setConfirmingRefusal(false)}
                />
            )}
        </div>
    );
};
